/**
 * Cart free gift promotion: before checkout (or buy now), add the free gift variant to the cart.
 * Uses document-level delegation so it keeps working after cart section morphing.
 */
(function () {
  const CART_ADD_URL = '/cart/add.js';
  const CART_JS_URL = '/cart.js';
  const FALLBACK_VARIANT_ID = '45798700089498';
  // Bundle products (treated as sets even though they are single line items in the cart)
  // When any of these are in the cart, they should count as 2+ products for the free-gift rule.
  const BUNDLE_PRODUCT_IDS = [
    8683658903706,
    8644832886938,
    8644819976346,
    8644817813658,
    8644815519898,
    8644782260378,
    8644765843610,
    8644756734106,
    8644738613402,
    8644702699674,
  ];

  /**
   * Decide if the cart qualifies for the free gift based on the current contents.
   * Rule: at least 2 non-gift line items in the cart.
   *
   * @param {Object} cart
   * @param {string} variantId
   * @returns {boolean}
   */
  function shouldAddGiftForCart(cart, variantId) {
    if (!cart || !cart.items || !cart.items.length) return false;
    var nonGiftQuantity = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (String(item.variant_id) !== String(variantId)) {
        var qty = Number(item.quantity) || 0;
        // If this line is one of our designated bundle products, treat it as representing 2+ units
        // so that a single bundle in the cart still qualifies for the free gift.
        var productId = Number(item.product_id || item.productId);
        var isBundleProduct = !isNaN(productId) && BUNDLE_PRODUCT_IDS.indexOf(productId) !== -1;
        if (isBundleProduct && qty > 0) {
          nonGiftQuantity += Math.max(2, qty);
        } else {
          nonGiftQuantity += qty;
        }
      }
    }
    // Eligible when there are at least 2 non-gift units (regardless of whether they are the same or different products)
    return nonGiftQuantity >= 2;
  }

  /**
   * Add the free gift variant to the cart if missing and eligibility rules pass.
   *
   * @param {string} variantId - free gift variant id
   * @returns {Promise<boolean>}
   */
  function addFreeGiftIfMissing(variantId) {
    if (!variantId) return Promise.resolve(true);

    var opts = { credentials: 'same-origin' };
    return fetch(CART_JS_URL, opts)
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hasGift = cart.items.some(function (item) {
          return String(item.variant_id) === String(variantId);
        });
        // If gift already present, or cart is not eligible, do nothing.
        if (hasGift || !shouldAddGiftForCart(cart, variantId)) return true;
        return fetch(CART_ADD_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
          .then(function (res) {
            var ok = res.ok;
            // If we successfully added the gift, trigger a cart:update so cart UI (including drawer)
            // re-renders to show the new line item.
            if (ok && typeof document !== 'undefined') {
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  detail: {
                    data: {},
                  },
                })
              );
            }
            return ok;
          })
          .catch(function () { return false; });
      })
      .catch(function () { return false; });
  }

  function getVariantIdFromContext(element) {
    if (!element) return FALLBACK_VARIANT_ID;
    var cartContext = element.closest && element.closest('cart-items-component');
    if (!cartContext && element.querySelector) {
      cartContext = element.querySelector('cart-items-component');
    }
    if (!cartContext && element.querySelector) {
      var ctas = element.querySelector('.cart__ctas[data-free-gift-variant-id]');
      return (ctas && ctas.getAttribute('data-free-gift-variant-id')) || FALLBACK_VARIANT_ID;
    }
    if (!cartContext) return FALLBACK_VARIANT_ID;
    var ctas = cartContext.querySelector('.cart__ctas[data-free-gift-variant-id]');
    return (ctas && ctas.getAttribute('data-free-gift-variant-id')) || FALLBACK_VARIANT_ID;
  }

  function goToCheckout() {
    window.location.href = '/checkout';
  }

  function handleCheckoutIntent(variantId, redirectUrl) {
    var url = redirectUrl || '/checkout';
    addFreeGiftIfMissing(variantId)
      .then(function () { window.location.href = url; })
      .catch(function () { window.location.href = url; });
  }

  // Whenever the cart is updated by the theme (items added/removed/changed),
  // check if we now qualify for the free gift and, if so, add it.
  // This keeps both the main cart and the cart drawer in sync with the promotion.
  if (typeof document !== 'undefined') {
    document.addEventListener(
      'cart:update',
      function () {
        var variantId = getVariantIdFromContext(document);
        addFreeGiftIfMissing(variantId);
      },
      false
    );
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.id !== 'cart-form') return;
    var submitter = e.submitter;
    if (submitter && submitter.getAttribute('name') !== 'checkout') return;
    e.preventDefault();
    e.stopPropagation();
    var variantId = getVariantIdFromContext(form);
    handleCheckoutIntent(variantId, '/checkout');
  }, true);

  // 1) Cart "Buy now" / accelerated: any click inside .cart__ctas .additional-checkout-buttons
  //    (Shopify renders buttons in shadow DOM so we catch by container)
  document.addEventListener('click', function (e) {
    var inCartAdditional = e.target.closest && e.target.closest('.cart__ctas .additional-checkout-buttons');
    if (!inCartAdditional) return;
    e.preventDefault();
    e.stopPropagation();
    var variantId = getVariantIdFromContext(inCartAdditional);
    handleCheckoutIntent(variantId, '/checkout');
  }, true);

  // 2) Main cart "Checkout" button
  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('#checkout, .cart__checkout-button');
    if (!button) return;
    if (button.getAttribute('form') !== 'cart-form') return;
    e.preventDefault();
    e.stopPropagation();
    var variantId = getVariantIdFromContext(button);
    handleCheckoutIntent(variantId, '/checkout');
  }, true);

  // 3) Product page "Buy it now" (unbranded): no special free-gift logic anymore,
  //    we now rely purely on cart contents (>= 2 products) like all other flows.
  document.addEventListener('click', function (e) {
    var wrapper = e.target.closest && e.target.closest('shopify-accelerated-checkout, .shopify-payment-button, .shopify-payment-button__button--unbranded');
    if (!wrapper) return;
    // Let Shopify handle Buy Now normally; free gift will be added later once cart has 2+ products
    return;
  }, true);

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.cart__ctas a[href*="checkout"]');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    e.stopPropagation();
    var variantId = getVariantIdFromContext(link);
    handleCheckoutIntent(variantId, href);
  }, true);

  if (typeof window !== 'undefined' && (window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart'))) {
    var ctas = document.querySelector('.cart__ctas[data-free-gift-variant-id]');
    var variantId = (ctas && ctas.getAttribute('data-free-gift-variant-id')) || FALLBACK_VARIANT_ID;
    addFreeGiftIfMissing(variantId);
  }
})();
